import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import {
  CloudArrowUpIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentIcon,
  XMarkIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  UsersIcon,
  TrashIcon,
  FolderArrowDownIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import CustomFormatBuilder, { DEFAULT_VA_FORMAT_FIELDS } from '../../components/CustomFormatBuilder';
import GoLoginJobProgressBar from '../../components/GoLoginJobProgressBar';
import { downloadVaAccountsColonExport } from '../../utils/vaAccountCsvExport';

const STATUS_OPTIONS = [
  { id: 'all', label: 'All statuses' },
  { id: 'active', label: 'Active' },
  { id: 'suspended', label: 'Suspended' },
  { id: 'captcha', label: 'Captcha' },
  { id: 'soft_ban', label: 'Soft ban' },
  { id: 'limited', label: 'Limited' }
];

const PERIOD_OPTIONS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: 'Last 7 days' },
  { id: 'custom', label: 'Custom' }
];

const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  suspended: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  captcha: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  soft_ban: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  limited: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200'
};

function formatDelta(value) {
  if (value > 0) return `+${value.toLocaleString()}`;
  return value.toLocaleString();
}

function shortText(value, maxLen) {
  const s = String(value ?? '');
  if (!s) return '—';
  if (s.length <= maxLen) return s;
  return `${s.slice(0, Math.max(0, maxLen - 1))}…`;
}

function shortAuth(value) {
  return shortText(value, 14);
}

function shortProxy(value) {
  const s = String(value ?? '').trim();
  if (!s) return '—';
  if (s.length <= 20) return s;
  return `${s.slice(0, 10)}…${s.slice(-6)}`;
}

function copyValueToClipboard(value, label) {
  const v = value == null ? '' : String(value);
  if (!v) return;
  navigator.clipboard.writeText(v);
  toast.success(`${label} copied`);
}

function accountToEditForm(item) {
  if (!item) {
    return {
      assignedVaId: '',
      username: '',
      plusUsername: '',
      password: '',
      phone: '',
      email: '',
      emailPassword: '',
      authToken: '',
      ct0: '',
      proxy: '',
      status: 'active',
      notes: ''
    };
  }
  return {
    assignedVaId: String(item.assignedVaId?._id || item.assignedVaId || ''),
    username: item.username || '',
    plusUsername: item.plusUsername || '',
    password: item.password || '',
    phone: item.phone || '',
    email: item.email || '',
    emailPassword: item.emailPassword || '',
    authToken: item.authToken || '',
    ct0: item.ct0 || '',
    proxy: item.proxy || '',
    status: item.status || 'active',
    notes: item.notes || ''
  };
}

const VAProfileAccountsSection = ({
  vaUsers,
  aiAgents = [],
  onStatusChange
}) => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });

  /**
   * Owner filter for the accounts list. The dropdown unifies human VAs
   * and AI VA Agents (each VAProfileAccount belongs to exactly one). The
   * selected value is encoded as 'all' | 'va:<id>' | 'agent:<id>' so the
   * <select> can mix both kinds via <optgroup>s. The request parameters
   * are derived in `ownerQueryParams` below.
   */
  const [filterOwnerKey, setFilterOwnerKey] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadVaId, setUploadVaId] = useState('');
  const [uploadText, setUploadText] = useState('');
  const [formatFields, setFormatFields] = useState(DEFAULT_VA_FORMAT_FIELDS);
  const [delimiter, setDelimiter] = useState(':');
  const [uploading, setUploading] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('active');
  /** 'va:<id>' | 'agent:<id>' — same encoding as the owner filter dropdown. */
  const [bulkOwnerKey, setBulkOwnerKey] = useState('');
  /**
   * Move-to-group control. '' = nothing chosen, '__remove__' = clear group,
   * '__new__' = prompt for a new group name, anything else = existing leaf
   * group name. Moves here are SILENT — they never fire the group's
   * automations (auto-assign to agent / preset); those only fire from
   * /dashboard/accounts.
   */
  const [bulkGroupChoice, setBulkGroupChoice] = useState('');
  const [groupOptions, setGroupOptions] = useState([]);

  const [showGologinModal, setShowGologinModal] = useState(false);
  const [gologinFolders, setGologinFolders] = useState([]);
  const [gologinFoldersLoading, setGologinFoldersLoading] = useState(false);
  const [gologinFolderMode, setGologinFolderMode] = useState('existing');
  const [gologinFolderName, setGologinFolderName] = useState('');
  const [gologinImportSpeed, setGologinImportSpeed] = useState(1);
  const [gologinImporting, setGologinImporting] = useState(false);
  const [gologinImportResult, setGologinImportResult] = useState(null);
  const [gologinJobId, setGologinJobId] = useState(null);
  const [gologinJobProgress, setGologinJobProgress] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAccountId, setEditingAccountId] = useState(null);
  const [editForm, setEditForm] = useState(accountToEditForm(null));
  const [savingEdit, setSavingEdit] = useState(false);

  const [exportUsernameMode, setExportUsernameMode] = useState('username');

  /**
   * Splits the composite owner key into the discrete query-string fields
   * the backend expects (`vaId` or `agentId`). Returns at most one of
   * them — never both. 'all' resolves to no owner filter at all.
   */
  const ownerQueryParams = useCallback(() => {
    if (!filterOwnerKey || filterOwnerKey === 'all') return { vaId: 'all' };
    const [kind, id] = filterOwnerKey.split(':');
    if (kind === 'agent' && id) return { agentId: id };
    if (kind === 'va' && id) return { vaId: id };
    return { vaId: 'all' };
  }, [filterOwnerKey]);

  const listFilterParams = useCallback(() => ({
    ...ownerQueryParams(),
    status: filterStatus,
    search: search || undefined
  }), [ownerQueryParams, filterStatus, search]);

  const queryParams = useCallback(() => ({
    ...listFilterParams(),
    period,
    from: period === 'custom' ? customFrom || undefined : undefined,
    to: period === 'custom' ? customTo || undefined : undefined,
    page: pagination.page,
    limit: pagination.limit
  }), [listFilterParams, period, customFrom, customTo, pagination.page, pagination.limit]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [filterOwnerKey, filterStatus, search]);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/va/profile-accounts', { params: queryParams() });
      if (res.data.success) {
        setItems(res.data.items || []);
        setPagination((p) => ({ ...p, ...(res.data.pagination || {}) }));
      }
    } catch (err) {
      toast.error('Failed to load profile accounts');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/va/profile-accounts/stats', {
        params: {
          ...ownerQueryParams(),
          status: filterStatus,
          period,
          from: period === 'custom' ? customFrom || undefined : undefined,
          to: period === 'custom' ? customTo || undefined : undefined
        }
      });
      if (res.data.success) setStats(res.data);
    } catch (err) {
      toast.error('Failed to load stats');
    }
  }, [ownerQueryParams, filterStatus, period, customFrom, customTo]);

  useEffect(() => {
    fetchAccounts();
    fetchStats();
  }, [fetchAccounts, fetchStats]);

  // Groups for the "Move to group" dropdown. Admin-scoped endpoint:
  // distinct Account.group across ALL automation accounts, so groups
  // owned by other dashboard users are listed too.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/admin/va/profile-accounts/groups');
        if (!cancelled) setGroupOptions(res.data?.groups || []);
      } catch (err) {
        // Non-fatal — dropdown just stays empty apart from remove/new options.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchGologinFolders = useCallback(async () => {
    setGologinFoldersLoading(true);
    try {
      const res = await api.get('/admin/va/gologin/folders');
      const folders = Array.isArray(res.data?.folders) ? res.data.folders : [];
      setGologinFolders(folders);
      return folders;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load GoLogin folders');
      setGologinFolders([]);
      return [];
    } finally {
      setGologinFoldersLoading(false);
    }
  }, []);

  const openGologinModal = async () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one account');
      return;
    }
    setGologinImportResult(null);
    setGologinJobId(null);
    setGologinJobProgress(null);
    setGologinFolderMode('existing');
    setGologinFolderName('');
    setShowGologinModal(true);
    const folders = await fetchGologinFolders();
    if (folders.length) {
      setGologinFolderName(folders[0].name || '');
    }
  };

  useEffect(() => {
    if (!gologinJobId) return undefined;

    let cancelled = false;
    const poll = async () => {
      while (!cancelled) {
        try {
          const res = await api.get(`/admin/va/gologin/jobs/${gologinJobId}`);
          if (cancelled) break;

          const job = res.data;
          setGologinJobProgress(job);

          if (job.status === 'completed') {
            const result = job.result || {};
            if (job.type === 'delete') {
              const gologinNote = result.gologinFailed
                ? ` · ${result.gologinFailed} GoLogin error(s)`
                : '';
              toast.success(
                `Removed ${result.deleted || 0} account(s), ${result.failed || 0} failed${gologinNote}`
              );
              setSelectedIds(new Set());
            } else if (job.type === 'import') {
              setGologinImportResult(result);
              const cookieNote = result.cookiesFailed
                ? ` · ${result.cookiesSet || 0} with AUTH cookie, ${result.cookiesFailed} missing cookie`
                : '';
              toast.success(
                `GoLogin: ${result.imported || 0} imported, ${result.skipped || 0} skipped, ${result.failed || 0} failed${cookieNote}`
              );
            } else {
              toast.success(`AUTH cookies synced: ${result.synced || 0} ok, ${result.failed || 0} failed`);
            }
            fetchAccounts();
            onStatusChange?.();
            setGologinImporting(false);
            break;
          }

          if (job.status === 'failed') {
            toast.error(job.error || 'GoLogin job failed');
            setGologinImporting(false);
            break;
          }
        } catch (err) {
          if (!cancelled) {
            toast.error(err.response?.data?.error || 'Failed to load job progress');
            setGologinImporting(false);
          }
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [gologinJobId, fetchAccounts, onStatusChange]);

  const startGologinJobPolling = (jobId) => {
    setGologinJobId(null);
    setGologinJobProgress(null);
    window.setTimeout(() => {
      setGologinJobId(jobId);
    }, 0);
  };

  const handleGologinClearImport = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(
      `Clear GoLogin import flags for ${selectedIds.size} selected account(s)? They can be imported to GoLogin again. This does not delete profiles in GoLogin.`
    )) return;
    setBulkLoading(true);
    try {
      const res = await api.post('/admin/va/profile-accounts/gologin-clear-import', {
        ids: Array.from(selectedIds)
      });
      const modified = res.data?.modified ?? 0;
      if (modified === 0) {
        toast.error('No selected accounts had GoLogin import flags');
      } else {
        toast.success(`Cleared GoLogin import flag on ${modified} account(s)`);
      }
      const selected = new Set(selectedIds);
      setItems((prev) => prev.map((row) => (
        selected.has(row._id)
          ? {
            ...row,
            gologinProfileId: '',
            gologinFolderName: '',
            gologinImportedAt: null,
            gologinCookieSet: false
          }
          : row
      )));
      onStatusChange?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to clear GoLogin import flags');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleGologinSyncCookies = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Re-push AUTH token cookies to GoLogin for ${selectedIds.size} selected account(s)?`)) return;
    setGologinImporting(true);
    setGologinImportResult(null);
    try {
      const res = await api.post('/admin/va/profile-accounts/gologin-sync-cookies', {
        ids: Array.from(selectedIds)
      });
      startGologinJobPolling(res.data.jobId);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Cookie sync failed');
      setGologinImporting(false);
    }
  };

  const handleGologinImport = async (e) => {
    e.preventDefault();
    const folderName = String(gologinFolderName || '').trim();
    if (!folderName) {
      toast.error('Enter or select a folder name');
      return;
    }
    setGologinImporting(true);
    setGologinImportResult(null);
    try {
      const res = await api.post('/admin/va/profile-accounts/gologin-import', {
        ids: Array.from(selectedIds),
        folderName,
        folderMode: gologinFolderMode,
        speed: gologinImportSpeed
      });
      startGologinJobPolling(res.data.jobId);
    } catch (err) {
      toast.error(err.response?.data?.error || 'GoLogin import failed');
      setGologinImporting(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadVaId) {
      toast.error('Select a VA to assign accounts to');
      return;
    }
    if (!uploadText.trim()) {
      toast.error('Paste at least one account line');
      return;
    }
    setUploading(true);
    try {
      const enabledFieldIds = formatFields.filter((f) => f.enabled).map((f) => f.id);
      const res = await api.post('/admin/va/profile-accounts/upload', {
        assignedVaId: uploadVaId,
        text: uploadText,
        formatFields: enabledFieldIds,
        delimiter: delimiter === '\t' || delimiter === '\\t' ? '\t' : delimiter
      });
      toast.success(`Uploaded ${res.data.count || 0} account(s)`);
      if (res.data.scanId) {
        toast.success('Initial stats scan queued');
      }
      setUploadText('');
      setShowUploadModal(false);
      setPagination((p) => ({ ...p, page: 1 }));
      fetchAccounts();
      fetchStats();
      onStatusChange?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this account from GoLogin and Wingr? Stats history will be preserved.')) return;
    setGologinImporting(true);
    setGologinImportResult(null);
    try {
      const res = await api.delete(`/admin/va/profile-accounts/${id}`);
      if (res.data?.jobId) {
        startGologinJobPolling(res.data.jobId);
      } else {
        toast.success('Account removed');
        fetchAccounts();
        fetchStats();
        onStatusChange?.();
        setGologinImporting(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
      setGologinImporting(false);
    }
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = async () => {
    const pageIds = items.map((i) => i._id);
    const scopeTotal = pagination.total || 0;
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
    const allScopeSelected = scopeTotal > 0 && selectedIds.size >= scopeTotal && allPageSelected;

    if (allScopeSelected) {
      setSelectedIds(new Set());
      return;
    }

    if (scopeTotal > pageIds.length) {
      setBulkLoading(true);
      try {
        const res = await api.get('/admin/va/profile-accounts/ids', { params: listFilterParams() });
        const ids = Array.isArray(res.data?.ids) ? res.data.ids : [];
        if (!ids.length) {
          toast.error('No accounts match the current filters');
          return;
        }
        setSelectedIds(new Set(ids));
        if (ids.length > pageIds.length) {
          toast.success(`Selected ${ids.length} accounts across all pages`);
        }
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to select all matching accounts');
      } finally {
        setBulkLoading(false);
      }
      return;
    }

    if (allPageSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(pageIds));
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (!window.confirm(
      `Remove ${count} selected account(s) from GoLogin and Wingr? Follower/follow-back stats will be preserved.`
    )) return;
    setGologinImporting(true);
    setGologinImportResult(null);
    try {
      const res = await api.post('/admin/va/profile-accounts/bulk-delete', {
        ids: Array.from(selectedIds)
      });
      startGologinJobPolling(res.data.jobId);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk delete failed');
      setGologinImporting(false);
    }
  };

  const handleBulkStatus = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const res = await api.patch('/admin/va/profile-accounts/bulk-status', {
        ids: Array.from(selectedIds),
        status: bulkStatus
      });
      toast.success(`Updated ${res.data.modified ?? selectedIds.size} account(s)`);
      fetchAccounts();
      onStatusChange?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk status update failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkAssign = async () => {
    if (selectedIds.size === 0) return;
    if (!bulkOwnerKey) {
      toast.error('Select a VA or AI VA Agent to assign to');
      return;
    }
    const [kind, ownerId] = bulkOwnerKey.split(':');
    if (!ownerId || (kind !== 'va' && kind !== 'agent')) {
      toast.error('Invalid owner selection');
      return;
    }
    setBulkLoading(true);
    try {
      const body = {
        ids: Array.from(selectedIds),
        ...(kind === 'va' ? { assignedVaId: ownerId } : { assignedAgentId: ownerId })
      };
      const res = await api.patch('/admin/va/profile-accounts/bulk-assign', body);
      toast.success(`Reassigned ${res.data.modified ?? selectedIds.size} account(s)`);
      setSelectedIds(new Set());
      setBulkOwnerKey('');
      fetchAccounts();
      onStatusChange?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk assign failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkMoveGroup = async () => {
    if (selectedIds.size === 0 || !bulkGroupChoice) return;

    let targetGroup = null;
    if (bulkGroupChoice === '__new__') {
      const name = window.prompt('Enter new group name:');
      if (!name || !name.trim()) return;
      targetGroup = name.trim();
    } else if (bulkGroupChoice !== '__remove__') {
      targetGroup = bulkGroupChoice;
    }

    setBulkLoading(true);
    try {
      const res = await api.patch('/admin/va/profile-accounts/bulk-move-group', {
        ids: Array.from(selectedIds),
        group: targetGroup
      });
      const { movedAccounts = 0, unresolved = 0 } = res.data || {};
      toast.success(
        targetGroup
          ? `Moved ${movedAccounts} account(s) to "${targetGroup}"${unresolved ? ` — ${unresolved} without a linked account skipped` : ''}`
          : `Removed ${movedAccounts} account(s) from their group${unresolved ? ` — ${unresolved} without a linked account skipped` : ''}`
      );
      if (targetGroup && !groupOptions.includes(targetGroup)) {
        setGroupOptions((prev) => [...prev, targetGroup].sort());
      }
      setBulkGroupChoice('');
      setSelectedIds(new Set());
      onStatusChange?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Move to group failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const openEditModal = () => {
    if (selectedIds.size !== 1) {
      toast.error('Select exactly one account to edit');
      return;
    }
    const id = Array.from(selectedIds)[0];
    const account = items.find((row) => row._id === id);
    if (!account) {
      toast.error('Selected account is not on this page');
      return;
    }
    setEditingAccountId(id);
    setEditForm(accountToEditForm(account));
    setShowEditModal(true);
  };

  const openEditModalForAccount = (account) => {
    setEditingAccountId(account._id);
    setEditForm(accountToEditForm(account));
    setShowEditModal(true);
  };

  const handleEditFieldChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingAccountId) return;
    setSavingEdit(true);
    try {
      const res = await api.patch(`/admin/va/profile-accounts/${editingAccountId}`, editForm);
      const updated = res.data?.item;
      if (updated) {
        setItems((prev) => prev.map((row) => (
          row._id === editingAccountId
            ? { ...row, ...updated, periodStats: row.periodStats }
            : row
        )));
      }
      toast.success('Account updated');
      setShowEditModal(false);
      setEditingAccountId(null);
      onStatusChange?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update account');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleExportSelected = async () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one account to export');
      return;
    }
    setBulkLoading(true);
    try {
      const ids = Array.from(selectedIds);
      const res = await api.post('/admin/va/profile-accounts/by-ids', { ids });
      const accounts = Array.isArray(res.data?.items) ? res.data.items : [];
      if (!accounts.length) {
        toast.error('No accounts found to export');
        return;
      }
      const usePlusUsername = exportUsernameMode === 'plusUsername';
      downloadVaAccountsColonExport(accounts, { usePlusUsername });
      toast.success(
        `Exported ${accounts.length} account(s) as ${usePlusUsername ? '+username' : 'username'}`
      );
    } catch (err) {
      toast.error(err.response?.data?.error || 'Export failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const pageIds = items.map((i) => i._id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const somePageSelected = pageIds.some((id) => selectedIds.has(id));
  const scopeTotal = pagination.total || 0;
  const allScopeSelected = scopeTotal > 0 && selectedIds.size >= scopeTotal && allPageSelected;

  const periodLabel = stats?.period?.label || 'today';
  const overall = stats?.overall || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Accounts</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Upload GoLogin credentials per VA. Follow and follow-back metrics for AI agents come from the automation ledger.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center gap-2 btn-primary"
          >
            <CloudArrowUpIcon className="w-5 h-5" />
            Upload accounts
          </button>
        </div>
      </div>

      {/* Stats overview */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            <UserGroupIcon className="w-4 h-4" />
            Accounts
          </div>
          <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
            {(overall.accountCount || 0).toLocaleString()}
          </div>
          <p className="text-xs text-gray-500 mt-1">Assigned profile accounts</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            <ArrowTrendingUpIcon className="w-4 h-4" />
            Following
          </div>
          <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
            {(overall.totalFollowing || 0).toLocaleString()}
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            {formatDelta(overall.followingDelta || 0)} in {periodLabel}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            <UsersIcon className="w-4 h-4" />
            Followers
          </div>
          <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
            {(overall.totalFollowers || 0).toLocaleString()}
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            {formatDelta(overall.followersDelta || 0)} in {periodLabel}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Period</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {PERIOD_OPTIONS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPeriod(p.id)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                  period === p.id
                    ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {period === 'custom' && (
            <div className="mt-3 flex flex-wrap gap-2">
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="form-input text-xs py-1" />
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="form-input text-xs py-1" />
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
        <select
          value={filterOwnerKey}
          onChange={(e) => { setFilterOwnerKey(e.target.value); setPagination((p) => ({ ...p, page: 1 })); }}
          className="form-select text-sm min-w-[200px]"
          title="Filter the list by who owns the profile account (a human VA or an AI VA Agent)"
        >
          <option value="all">All VAs &amp; AI Agents</option>
          {vaUsers.length > 0 && (
            <optgroup label="Virtual Assistants">
              {vaUsers.map((va) => (
                <option key={`va:${va._id}`} value={`va:${va._id}`}>{va.username}</option>
              ))}
            </optgroup>
          )}
          {aiAgents.length > 0 && (
            <optgroup label="AI VA Agents">
              {aiAgents.map((agent) => (
                <option key={`agent:${agent._id}`} value={`agent:${agent._id}`}>
                  {agent.name}
                  {typeof agent.accountCount === 'number' ? ` (${agent.accountCount})` : ''}
                </option>
              ))}
            </optgroup>
          )}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setPagination((p) => ({ ...p, page: 1 })); }}
          className="form-select text-sm"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search username, +username, email, phone..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPagination((p) => ({ ...p, page: 1 })); }}
            className="form-input pl-9 w-full text-sm"
          />
        </div>
      </div>

      {/* GoLogin job progress (import, cookie sync, or delete) */}
      {gologinJobProgress && gologinImporting && !showGologinModal && (
        <GoLoginJobProgressBar
          progress={gologinJobProgress}
          title={
            gologinJobProgress.type === 'delete'
              ? 'Removing from GoLogin + Wingr…'
              : gologinJobProgress.type === 'sync-cookies'
                ? 'Syncing AUTH cookies…'
                : `Importing to GoLogin${gologinJobProgress.speed > 1 ? ` (${gologinJobProgress.speed}×)` : ''}…`
          }
        />
      )}

      {/* Bulk actions */}
      {items.length > 0 && (
        <div className="flex flex-wrap gap-3 items-center bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
          <label className="flex items-center gap-2 text-sm whitespace-nowrap">
            <input
              type="checkbox"
              checked={allScopeSelected || (allPageSelected && scopeTotal <= pageIds.length)}
              ref={(el) => {
                if (el) el.indeterminate = somePageSelected && !allPageSelected && !allScopeSelected;
              }}
              onChange={handleSelectAll}
              disabled={bulkLoading}
              className="rounded"
            />
            <span>
              {selectedIds.size > 0
                ? `${selectedIds.size} selected`
                : 'Select all'}
              {scopeTotal > 0 ? ` · ${scopeTotal} matching` : ''}
            </span>
          </label>
          <div className="h-5 w-px bg-gray-200 dark:bg-gray-600 hidden sm:block" />
          <select
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value)}
            className="form-select text-sm"
            disabled={bulkLoading}
          >
            {STATUS_OPTIONS.filter((s) => s.id !== 'all').map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={openEditModal}
            disabled={selectedIds.size !== 1 || bulkLoading}
            className="inline-flex items-center gap-1.5 btn-secondary !py-1.5 text-sm disabled:opacity-40"
          >
            <PencilSquareIcon className="w-4 h-4" />
            Edit
          </button>
          <select
            value={exportUsernameMode}
            onChange={(e) => setExportUsernameMode(e.target.value)}
            className="form-select text-sm"
            disabled={bulkLoading}
            title="First field in export line"
          >
            <option value="username">Export with username</option>
            <option value="plusUsername">Export with +username</option>
          </select>
          <button
            type="button"
            onClick={handleExportSelected}
            disabled={selectedIds.size === 0 || bulkLoading}
            className="inline-flex items-center gap-1.5 btn-secondary !py-1.5 text-sm disabled:opacity-40"
            title="username:password:phone:ct0:auth:proxy"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            Export selected
          </button>
          <button
            type="button"
            onClick={handleBulkStatus}
            disabled={selectedIds.size === 0 || bulkLoading}
            className="btn-secondary !py-1.5 text-sm disabled:opacity-40"
          >
            Set status
          </button>
          <select
            value={bulkOwnerKey}
            onChange={(e) => setBulkOwnerKey(e.target.value)}
            className="form-select text-sm min-w-[180px]"
            disabled={bulkLoading}
            title="Reassign selected accounts to a human VA or an AI VA Agent"
          >
            <option value="">Reassign to VA / AI Agent…</option>
            {vaUsers.length > 0 && (
              <optgroup label="Virtual Assistants">
                {vaUsers.map((va) => (
                  <option key={`va:${va._id}`} value={`va:${va._id}`}>
                    {va.username}
                    {va.vaProfile?.displayName ? ` (${va.vaProfile.displayName})` : ''}
                  </option>
                ))}
              </optgroup>
            )}
            {aiAgents.length > 0 && (
              <optgroup label="AI VA Agents">
                {aiAgents.map((agent) => (
                  <option key={`agent:${agent._id}`} value={`agent:${agent._id}`}>
                    {agent.name}
                    {typeof agent.accountCount === 'number' ? ` (${agent.accountCount})` : ''}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <button
            type="button"
            onClick={handleBulkAssign}
            disabled={selectedIds.size === 0 || !bulkOwnerKey || bulkLoading}
            className="btn-secondary !py-1.5 text-sm disabled:opacity-40"
          >
            Reassign
          </button>
          <select
            value={bulkGroupChoice}
            onChange={(e) => setBulkGroupChoice(e.target.value)}
            className="form-select text-sm min-w-[160px]"
            disabled={bulkLoading}
            title="Move linked accounts to another group (silent — does not fire the group's auto-assign / preset automations)"
          >
            <option value="">Move to group…</option>
            <option value="__remove__">— Remove from group —</option>
            {groupOptions.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
            <option value="__new__">+ New group…</option>
          </select>
          <button
            type="button"
            onClick={handleBulkMoveGroup}
            disabled={selectedIds.size === 0 || !bulkGroupChoice || bulkLoading}
            className="btn-secondary !py-1.5 text-sm disabled:opacity-40"
            title="Updates Account.group only — group automations are not triggered from this page"
          >
            Move
          </button>
          <button
            type="button"
            onClick={openGologinModal}
            disabled={selectedIds.size === 0 || bulkLoading || gologinImporting}
            className="inline-flex items-center gap-1.5 btn-primary !py-1.5 text-sm disabled:opacity-40"
          >
            <FolderArrowDownIcon className="w-4 h-4" />
            Import to GoLogin
          </button>
          <button
            type="button"
            onClick={handleGologinSyncCookies}
            disabled={selectedIds.size === 0 || bulkLoading || gologinImporting}
            className="btn-secondary !py-1.5 text-sm disabled:opacity-40"
          >
            Re-sync AUTH cookies
          </button>
          <button
            type="button"
            onClick={handleGologinClearImport}
            disabled={selectedIds.size === 0 || bulkLoading || gologinImporting}
            className="inline-flex items-center gap-1.5 btn-secondary !py-1.5 text-sm disabled:opacity-40"
            title="Clear imported flag so accounts can be re-imported to GoLogin"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Clear GoLogin flags
          </button>
          <button
            type="button"
            onClick={handleBulkDelete}
            disabled={selectedIds.size === 0 || bulkLoading || gologinImporting}
            className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 dark:text-red-400 disabled:opacity-40"
            title="Remove from GoLogin first, then soft-delete on Wingr (stats preserved)"
          >
            <TrashIcon className="w-4 h-4" />
            Delete selected
          </button>
          {selectedIds.size > 0 && (
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkLoading}
              className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* List */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading accounts...</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No profile accounts match your filters.</div>
        ) : (
          <table className="min-w-[1050px] w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-left text-[11px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = somePageSelected && !allPageSelected;
                    }}
                    onChange={() => {
                      if (allPageSelected) {
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          pageIds.forEach((id) => next.delete(id));
                          return next;
                        });
                      } else {
                        setSelectedIds((prev) => new Set([...prev, ...pageIds]));
                      }
                    }}
                    className="rounded"
                  />
                </th>
                <th className="px-3 py-2">VA</th>
                <th className="px-3 py-2">Username</th>
                <th className="px-3 py-2">+Username</th>
                <th className="px-3 py-2">Password</th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">AUTH</th>
                <th className="px-3 py-2 min-w-[120px]">Proxy</th>
                <th className="px-3 py-2">Following</th>
                <th className="px-3 py-2">Followers</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">GoLogin</th>
                <th className="px-3 py-2 w-24" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {items.map((item) => (
                <tr key={item._id} className={`align-top ${selectedIds.has(item._id) ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : ''}`}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item._id)}
                      onChange={() => toggleOne(item._id)}
                      className="rounded"
                    />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{item.assignedVaId?.username || '—'}</span>
                      <button
                        type="button"
                        title="Copy VA"
                        className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyValueToClipboard(item.assignedVaId?.username, 'VA');
                        }}
                      >
                        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] max-w-[85px] truncate" title={item.username}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{item.username}</span>
                      <button
                        type="button"
                        title="Copy Username"
                        className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); copyValueToClipboard(item.username, 'Username'); }}
                      >
                        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] max-w-[85px] truncate" title={item.plusUsername || ''}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-emerald-700 dark:text-emerald-300">
                        {item.plusUsername ? `+${item.plusUsername}` : '—'}
                      </span>
                      {item.plusUsername && (
                        <button
                          type="button"
                          title="Copy +Username"
                          className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 flex-shrink-0"
                          onClick={(e) => { e.stopPropagation(); copyValueToClipboard(item.plusUsername, '+Username'); }}
                        >
                          <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] max-w-[95px] truncate" title={item.password}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{shortText(item.password, 18)}</span>
                      <button
                        type="button"
                        title="Copy Password"
                        className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); copyValueToClipboard(item.password, 'Password'); }}
                      >
                        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] max-w-[90px] truncate" title={item.phone}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{item.phone || '—'}</span>
                      <button
                        type="button"
                        title="Copy Phone"
                        className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); copyValueToClipboard(item.phone, 'Phone'); }}
                      >
                        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] max-w-[120px] truncate" title={item.email}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{item.email || '—'}</span>
                      <button
                        type="button"
                        title="Copy Email"
                        className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); copyValueToClipboard(item.email, 'Email'); }}
                      >
                        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] max-w-[95px] truncate" title={item.authToken}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{item.authToken ? shortAuth(item.authToken) : '—'}</span>
                      <button
                        type="button"
                        title="Copy AUTH"
                        className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); copyValueToClipboard(item.authToken, 'AUTH'); }}
                      >
                        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] max-w-[120px] truncate" title={item.proxy}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{item.proxy ? shortProxy(item.proxy) : '—'}</span>
                      <button
                        type="button"
                        title="Copy Proxy"
                        className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); copyValueToClipboard(item.proxy, 'Proxy'); }}
                      >
                        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center justify-between gap-2">
                      <div>{(item.followingCount || 0).toLocaleString()}</div>
                      <button
                        type="button"
                        title="Copy Following"
                        className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); copyValueToClipboard(item.followingCount, 'Following'); }}
                      >
                        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="text-xs text-emerald-600 dark:text-emerald-400">
                      {formatDelta(item.periodStats?.followingDelta || 0)} ({periodLabel})
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center justify-between gap-2">
                      <div>{(item.followersCount || 0).toLocaleString()}</div>
                      <button
                        type="button"
                        title="Copy Followers"
                        className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); copyValueToClipboard(item.followersCount, 'Followers'); }}
                      >
                        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="text-xs text-emerald-600 dark:text-emerald-400">
                      {formatDelta(item.periodStats?.followersDelta || 0)} ({periodLabel})
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[item.status] || ''}`}>
                        {item.status}
                      </span>
                      <button
                        type="button"
                        title="Copy Status"
                        className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); copyValueToClipboard(item.status, 'Status'); }}
                      >
                        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {item.gologinProfileId ? (
                      <span
                        className="px-2 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200"
                        title={item.gologinFolderName ? `Folder: ${item.gologinFolderName}` : item.gologinProfileId}
                      >
                        Imported
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => openEditModalForAccount(item)}
                      className="text-xs text-indigo-600 hover:underline mr-2"
                    >
                      Edit
                    </button>
                    <button type="button" onClick={() => handleDelete(item._id)} className="text-xs text-red-600 hover:underline">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pagination.total > pagination.limit && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">
            Page {pagination.page} · {pagination.total} total
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
              className="btn-secondary !py-1 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={pagination.page * pagination.limit >= pagination.total}
              onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
              className="btn-secondary !py-1 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* GoLogin import modal */}
      {showGologinModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => !gologinImporting && setShowGologinModal(false)}
            />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full relative z-10">
              <div className="px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Import to GoLogin</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Create GoLogin profiles for {selectedIds.size} selected account(s). Each profile opens x.com and gets the AUTH token cookie.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => !gologinImporting && setShowGologinModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                    disabled={gologinImporting}
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleGologinImport} className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Folder</p>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="gologinFolderMode"
                        checked={gologinFolderMode === 'existing'}
                        onChange={() => setGologinFolderMode('existing')}
                        disabled={gologinImporting}
                      />
                      Use existing folder
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="gologinFolderMode"
                        checked={gologinFolderMode === 'new'}
                        onChange={() => setGologinFolderMode('new')}
                        disabled={gologinImporting}
                      />
                      Create new folder
                    </label>
                  </div>

                  {gologinFolderMode === 'existing' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Select folder
                      </label>
                      {gologinFoldersLoading ? (
                        <p className="text-sm text-gray-500">Loading folders…</p>
                      ) : (
                        <select
                          value={gologinFolderName}
                          onChange={(e) => setGologinFolderName(e.target.value)}
                          className="form-select w-full"
                          required
                          disabled={gologinImporting || gologinFolders.length === 0}
                        >
                          {gologinFolders.length === 0 ? (
                            <option value="">No folders found — create a new one</option>
                          ) : (
                            gologinFolders.map((folder) => (
                              <option key={folder.id || folder.name} value={folder.name}>
                                {folder.name}
                              </option>
                            ))
                          )}
                        </select>
                      )}
                      <button
                        type="button"
                        onClick={fetchGologinFolders}
                        disabled={gologinFoldersLoading || gologinImporting}
                        className="mt-2 text-xs text-indigo-600 hover:underline disabled:opacity-50"
                      >
                        Refresh folder list
                      </button>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        New folder name
                      </label>
                      <input
                        type="text"
                        value={gologinFolderName}
                        onChange={(e) => setGologinFolderName(e.target.value)}
                        className="form-input w-full"
                        placeholder="e.g. VA - Maria"
                        required
                        disabled={gologinImporting}
                      />
                    </div>
                  )}

                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Import speed</p>
                    <div className="flex flex-wrap gap-2">
                      {[1, 2, 3, 5, 10].map((speed) => (
                        <label
                          key={speed}
                          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                            gologinImportSpeed === speed
                              ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                              : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          } ${gologinImporting ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <input
                            type="radio"
                            name="gologinImportSpeed"
                            value={speed}
                            checked={gologinImportSpeed === speed}
                            onChange={() => setGologinImportSpeed(speed)}
                            disabled={gologinImporting}
                            className="sr-only"
                          />
                          {speed}×
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Higher speeds shorten delays between GoLogin API uploads. Use 2×–3× for large batches; 5×–10× may hit rate limits.
                    </p>
                  </div>

                  {(gologinImporting || gologinJobProgress) && (
                    <GoLoginJobProgressBar
                      progress={gologinJobProgress}
                      title={`Importing to GoLogin${(gologinJobProgress?.speed || gologinImportSpeed) > 1 ? ` (${gologinJobProgress?.speed || gologinImportSpeed}×)` : ''}…`}
                    />
                  )}

                  {gologinImportResult && !gologinImporting && (
                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3 text-sm space-y-1">
                      <p>
                        <span className="font-medium">Folder:</span> {gologinImportResult.folderName}
                      </p>
                      <p className="text-emerald-600">{gologinImportResult.imported} imported</p>
                      {(gologinImportResult.cookiesSet || 0) > 0 && (
                        <p className="text-emerald-700">{gologinImportResult.cookiesSet} with AUTH token cookie</p>
                      )}
                      {(gologinImportResult.cookiesFailed || 0) > 0 && (
                        <p className="text-amber-700">
                          {gologinImportResult.cookiesFailed} profile(s) created but GoLogin did not save the AUTH cookie — use &quot;Re-sync AUTH cookies&quot; on those accounts
                        </p>
                      )}
                      {(gologinImportResult.skipped || 0) > 0 && (
                        <p className="text-amber-600">{gologinImportResult.skipped} skipped (already in GoLogin)</p>
                      )}
                      {(gologinImportResult.failed || 0) > 0 && (
                        <p className="text-red-600">{gologinImportResult.failed} failed</p>
                      )}
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowGologinModal(false)}
                      className="btn-secondary"
                      disabled={gologinImporting}
                    >
                      {gologinImportResult ? 'Close' : 'Cancel'}
                    </button>
                    <button type="submit" disabled={gologinImporting} className="btn-primary disabled:opacity-50">
                      {gologinImporting ? `Importing… ${gologinJobProgress?.processed || 0}/${gologinJobProgress?.total || selectedIds.size}` : `Import ${selectedIds.size} account(s)`}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit account modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => !savingEdit && setShowEditModal(false)}
            />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full relative z-10">
              <div className="px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Edit account</h3>
                    <p className="text-sm text-gray-500 mt-1">Update credentials and assignment for this profile account.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => !savingEdit && setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSaveEdit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Assigned VA</label>
                      <select
                        value={editForm.assignedVaId}
                        onChange={(e) => handleEditFieldChange('assignedVaId', e.target.value)}
                        className="form-select w-full"
                        required
                        disabled={savingEdit}
                      >
                        <option value="">Select VA…</option>
                        {vaUsers.map((va) => (
                          <option key={va._id} value={va._id}>
                            {va.username}{va.vaProfile?.displayName ? ` (${va.vaProfile.displayName})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Username</label>
                      <input
                        type="text"
                        value={editForm.username}
                        onChange={(e) => handleEditFieldChange('username', e.target.value)}
                        className="form-input w-full font-mono text-sm"
                        required
                        disabled={savingEdit}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">+Username</label>
                      <input
                        type="text"
                        value={editForm.plusUsername}
                        onChange={(e) => handleEditFieldChange('plusUsername', e.target.value.replace(/^@/, ''))}
                        className="form-input w-full font-mono text-sm"
                        placeholder="updated handle"
                        disabled={savingEdit}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
                      <input
                        type="text"
                        value={editForm.password}
                        onChange={(e) => handleEditFieldChange('password', e.target.value)}
                        className="form-input w-full font-mono text-sm"
                        required
                        disabled={savingEdit}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                      <select
                        value={editForm.status}
                        onChange={(e) => handleEditFieldChange('status', e.target.value)}
                        className="form-select w-full"
                        disabled={savingEdit}
                      >
                        {STATUS_OPTIONS.filter((s) => s.id !== 'all').map((s) => (
                          <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                      <input
                        type="text"
                        value={editForm.phone}
                        onChange={(e) => handleEditFieldChange('phone', e.target.value)}
                        className="form-input w-full font-mono text-sm"
                        disabled={savingEdit}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                      <input
                        type="text"
                        value={editForm.email}
                        onChange={(e) => handleEditFieldChange('email', e.target.value)}
                        className="form-input w-full font-mono text-sm"
                        disabled={savingEdit}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email password</label>
                      <input
                        type="text"
                        value={editForm.emailPassword}
                        onChange={(e) => handleEditFieldChange('emailPassword', e.target.value)}
                        className="form-input w-full font-mono text-sm"
                        disabled={savingEdit}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">AUTH token</label>
                      <textarea
                        value={editForm.authToken}
                        onChange={(e) => handleEditFieldChange('authToken', e.target.value)}
                        className="form-input w-full font-mono text-xs min-h-[72px]"
                        disabled={savingEdit}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CT0</label>
                      <textarea
                        value={editForm.ct0}
                        onChange={(e) => handleEditFieldChange('ct0', e.target.value)}
                        className="form-input w-full font-mono text-xs min-h-[72px]"
                        disabled={savingEdit}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Proxy</label>
                      <input
                        type="text"
                        value={editForm.proxy}
                        onChange={(e) => handleEditFieldChange('proxy', e.target.value)}
                        className="form-input w-full font-mono text-sm"
                        placeholder="host:port:user:pass"
                        disabled={savingEdit}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                      <textarea
                        value={editForm.notes}
                        onChange={(e) => handleEditFieldChange('notes', e.target.value)}
                        className="form-input w-full text-sm min-h-[64px]"
                        disabled={savingEdit}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="btn-secondary"
                      disabled={savingEdit}
                    >
                      Cancel
                    </button>
                    <button type="submit" disabled={savingEdit} className="btn-primary disabled:opacity-50">
                      {savingEdit ? 'Saving…' : 'Save changes'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowUploadModal(false)} />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full relative z-10">
              <div className="px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Upload Accounts</h3>
                    <p className="text-sm text-gray-500 mt-1">Assign GoLogin credentials to a virtual assistant.</p>
                  </div>
                  <button type="button" onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-gray-600">
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleUpload} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Assign to VA</label>
                    <select
                      value={uploadVaId}
                      onChange={(e) => setUploadVaId(e.target.value)}
                      className="form-select w-full"
                      required
                    >
                      <option value="">Select VA...</option>
                      {vaUsers.map((va) => (
                        <option key={va._id} value={va._id}>{va.username} {va.vaProfile?.displayName ? `(${va.vaProfile.displayName})` : ''}</option>
                      ))}
                    </select>
                  </div>

                  <CustomFormatBuilder
                    fields={formatFields}
                    onFieldsChange={setFormatFields}
                    delimiter={delimiter}
                    onDelimiterChange={setDelimiter}
                    accountText={uploadText}
                    onAccountTextChange={setUploadText}
                  />

                  <div className="flex justify-end gap-3 pt-2">
                    <button type="button" onClick={() => setShowUploadModal(false)} className="btn-secondary">
                      Cancel
                    </button>
                    <button type="submit" disabled={uploading} className="btn-primary disabled:opacity-50">
                      {uploading ? 'Uploading...' : 'Upload accounts'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VAProfileAccountsSection;
